import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Chip,
  Divider,
  Skeleton,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { createApiUrl } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';

function SummarySkeleton() {
  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Skeleton variant="rounded" width={160} height={22} animation="wave" />
        <Skeleton variant="rounded" width={120} height={22} animation="wave" />
        <Skeleton variant="rounded" width={180} height={22} animation="wave" />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Skeleton variant="text" width="55%" height={28} animation="wave" sx={{ mb: 1 }} />
      <Skeleton variant="text" width="100%" height={18} animation="wave" />
      <Skeleton variant="text" width="92%" height={18} animation="wave" />
      <Skeleton variant="text" width="85%" height={18} animation="wave" />
      <Skeleton variant="text" width="97%" height={18} animation="wave" />
      <Skeleton variant="text" width="60%" height={18} animation="wave" sx={{ mb: 2 }} />
      <Skeleton variant="text" width="45%" height={28} animation="wave" sx={{ mb: 1 }} />
      <Skeleton variant="text" width="88%" height={18} animation="wave" />
      <Skeleton variant="text" width="78%" height={18} animation="wave" />
      <Skeleton variant="text" width="94%" height={18} animation="wave" />
      <Skeleton variant="text" width="70%" height={18} animation="wave" sx={{ mb: 2 }} />
      <Skeleton variant="text" width="50%" height={28} animation="wave" sx={{ mb: 1 }} />
      <Skeleton variant="text" width="90%" height={18} animation="wave" />
      <Skeleton variant="text" width="82%" height={18} animation="wave" />
      <Skeleton variant="text" width="65%" height={18} animation="wave" />
    </Box>
  );
}

export default function AISummaryWindow() {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const patientId = params.get('patientId');
  const initialPromptName = params.get('promptName');
  const initialExamId = params.get('examId');
  const storageKey = params.get('storageKey');
  const initialCustomPrompt = storageKey ? localStorage.getItem(storageKey) : null;

  const [activePromptName, setActivePromptName] = useState(initialPromptName);
  const [activeCustomPrompt, setActiveCustomPrompt] = useState(initialCustomPrompt);
  const [activeExamId, setActiveExamId] = useState(initialExamId);
  const [userPrompts, setUserPrompts] = useState([]);
  const [promptMenuAnchor, setPromptMenuAnchor] = useState(null);

  const effectivePromptName = activePromptName || '__system__';

  useEffect(() => {
    if (activePromptName) {
      document.title = `KI Summary – ${activePromptName}`;
    } else {
      document.title = 'KI Dokumentenzusammenfassung';
    }
  }, [activePromptName]);

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const response = await fetch(createApiUrl('profile/ai-settings'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setUserPrompts(data.ai_settings?.prompts || []);
        }
      } catch (e) {
        console.error('[AISummaryWindow] Error loading prompts:', e);
      }
    };
    loadPrompts();
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!patientId) return null;
    try {
      let url = `ai-summary/patient/${patientId}?prompt_name=${encodeURIComponent(effectivePromptName)}`;
      if (activeExamId) url += `&exam_id=${activeExamId}`;
      const response = await fetch(createApiUrl(url), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
        setError(null);
        return data;
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('[AISummary] Fetch error:', err);
      return null;
    }
  }, [patientId, effectivePromptName, activeExamId]);

  const pollStatus = useCallback(async () => {
    if (!patientId) return;
    try {
      let statusUrl = `ai-summary/patient/${patientId}/status?prompt_name=${encodeURIComponent(effectivePromptName)}`;
      if (activeExamId) statusUrl += `&exam_id=${activeExamId}`;
      const response = await fetch(createApiUrl(statusUrl), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'COMPLETED' || data.status === 'ERROR') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          await fetchSummary();
          setRegenerating(false);
        }
      }
    } catch (err) {
      console.error('[AISummary] Poll error:', err);
    }
  }, [patientId, fetchSummary]);

  const startGeneration = useCallback(async () => {
    if (!patientId) return;
    setRegenerating(true);
    setError(null);
    try {
      const lang = (i18n.language || 'de').substring(0, 2);
      const body = { prompt_name: effectivePromptName };
      if (activeCustomPrompt) body.custom_prompt = activeCustomPrompt;
      if (activeExamId) body.exam_id = parseInt(activeExamId, 10);

      const response = await fetch(createApiUrl(`ai-summary/patient/${patientId}/generate?language=${lang}`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(pollStatus, 2000);
    } catch (err) {
      setError(err.message);
      setRegenerating(false);
    }
  }, [patientId, i18n.language, pollStatus, activeCustomPrompt, effectivePromptName, activeExamId]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setSummary(null);
    setRegenerating(false);

    fetchSummary().then((data) => {
      setLoading(false);
      if (data && (data.status === 'PENDING' || data.status === 'GENERATING')) {
        setRegenerating(true);
        pollingRef.current = setInterval(pollStatus, 2000);
      } else if (!data) {
        startGeneration();
      }
    });

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [patientId, fetchSummary, pollStatus, startGeneration]);

  useEffect(() => {
    return () => {
      if (storageKey) localStorage.removeItem(storageKey);
    };
  }, [storageKey]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString(
        i18n.language === 'en' ? 'en-GB' : i18n.language === 'fr' ? 'fr-FR' : 'de-DE'
      );
    } catch {
      return dateStr;
    }
  };

  const handleSwitchPrompt = (newPromptName, newCustomPrompt, newScope) => {
    setPromptMenuAnchor(null);
    if (newPromptName === activePromptName) return;
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setActivePromptName(newPromptName);
    setActiveCustomPrompt(newCustomPrompt);
    setActiveExamId(newScope === 'current_exam' ? initialExamId : null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'color-mix(in srgb, var(--rd-primary) 6%, transparent)',
            borderRadius: 0,
            flexShrink: 0
          }}
        >
          <AutoAwesomeIcon sx={{ color: 'var(--rd-primary)', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {t('aiSummary.title')}
          </Typography>
          <Chip
            size="small"
            label={activePromptName ? t('aiSummary.promptUsed', { name: activePromptName }) : t('aiSummary.systemDefault')}
            onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
            onDelete={(e) => setPromptMenuAnchor(e.currentTarget)}
            deleteIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 22, fontSize: '0.7rem',
              backgroundColor: 'color-mix(in srgb, var(--rd-primary) 15%, transparent)', color: 'var(--rd-primary)',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'color-mix(in srgb, var(--rd-primary) 25%, transparent)' },
              '& .MuiChip-deleteIcon': { color: 'var(--rd-primary)' }
            }}
          />
          <IconButton size="small" onClick={() => window.close()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
          {(loading || regenerating) && <SummarySkeleton />}

          {!loading && !summary && !error && !regenerating && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('aiSummary.noSummary')}
            </Typography>
          )}

          {error && !loading && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('aiSummary.error')}: {error}
            </Alert>
          )}

          {summary?.status === 'ERROR' && !loading && !regenerating && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {summary.error_message || t('aiSummary.error')}
            </Alert>
          )}

          {summary?.status === 'COMPLETED' && !loading && !regenerating && (
            <Box>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                {summary.document_count > 0 && (
                  <Chip size="small" label={t('aiSummary.documentCount', { count: summary.document_count })} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                )}
                {summary.model_used && (
                  <Chip size="small" label={t('aiSummary.model', { model: summary.model_used })} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                )}
                {summary.updated_at && (
                  <Chip size="small" label={t('aiSummary.generatedAt', { date: formatDate(summary.updated_at) })} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                )}
                {summary.prompt_name && summary.prompt_name !== '__system__' && (
                  <Chip size="small" label={t('aiSummary.promptUsed', { name: summary.prompt_name })} sx={{ height: 22, fontSize: '0.7rem', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 15%, transparent)', color: 'var(--rd-primary)' }} />
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{
                '& h1, & h2, & h3': { fontSize: '0.95rem', fontWeight: 700, mt: 2, mb: 0.5, color: 'var(--rd-primary)' },
                '& h4, & h5, & h6': { fontSize: '0.85rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                '& p': { fontSize: '0.83rem', lineHeight: 1.6, my: 0.5 },
                '& ul, & ol': { pl: 2.5, my: 0.5 },
                '& li': { fontSize: '0.83rem', lineHeight: 1.5, mb: 0.3 },
                '& strong': { fontWeight: 600 },
                '& hr': { my: 1.5, borderColor: 'divider' },
              }}>
                <ReactMarkdown>{summary.summary_text}</ReactMarkdown>
              </Box>
              <Divider sx={{ mt: 2, mb: 1 }} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.4, display: 'block' }}>
                {t('aiSummary.disclaimer')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex', justifyContent: 'flex-end', gap: 1,
            px: 2, py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            flexShrink: 0
          }}
        >
          <Button
            onClick={startGeneration}
            disabled={regenerating || loading}
            startIcon={<RefreshIcon />}
            size="small"
            sx={{ color: 'var(--rd-primary)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)' } }}
          >
            {t('aiSummary.regenerate')}
          </Button>
        </Paper>
      </Box>
      <Menu
        open={Boolean(promptMenuAnchor)}
        anchorEl={promptMenuAnchor}
        onClose={() => setPromptMenuAnchor(null)}
      >
        <MenuItem
          selected={!activePromptName}
          onClick={() => handleSwitchPrompt(null, null, 'full_history')}
        >
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('aiSummary.systemDefault')}</ListItemText>
        </MenuItem>
        {userPrompts.map((p) => (
          <MenuItem
            key={p.id}
            selected={activePromptName === p.name}
            onClick={() => handleSwitchPrompt(p.name, p.prompt, p.scope)}
          >
            <ListItemText inset>{p.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
